import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Check, Mic, Type, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuthStore, useUIStore } from '../lib/store';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, updateSettings } = useAuthStore();
  const { language, setLanguage, mode, setMode } = useUIStore();
  const { t } = useTranslation(language);

  const [step, setStep] = useState(1);
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [selectedMode, setSelectedMode] = useState(mode);
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const languages = [
    { code: 'en', name: 'English', native: 'English' },
    { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
    { code: 'es', name: 'Spanish', native: 'Español' },
    { code: 'ar', name: 'Arabic', native: 'العربية' },
  ];

  const modes = [
    {
      id: 'simple',
      title: t('simple_mode'),
      description: t('simple_mode_desc'),
      icon: Sparkles,
    },
    {
      id: 'advanced',
      title: t('advanced_mode'),
      description: t('advanced_mode_desc'),
      icon: Type,
    },
  ];

  const handleContinue = async () => {
    if (step === 1) {
      setLanguage(selectedLanguage);
      setStep(2);
    } else {
      setIsLoading(true);
      try {
        setMode(selectedMode);
        await updateSettings({ language: selectedLanguage, mode: selectedMode });
        toast.success(t('success'));
        navigate('/app');
      } catch (error) {
        toast.error(error.message || t('error'));
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center">
            <Sparkles className="h-7 w-7 text-primary-foreground" />
          </div>
          <span className="font-heading font-bold text-2xl">OSLO One</span>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className={`h-2 w-16 rounded-full ${step >= 1 ? 'bg-primary' : 'bg-secondary'}`} />
          <div className={`h-2 w-16 rounded-full ${step >= 2 ? 'bg-primary' : 'bg-secondary'}`} />
        </div>

        {/* Content Card */}
        <div className="glass rounded-2xl p-8">
          <h1 className="font-heading text-3xl font-bold text-center mb-2">
            {t('onboarding_title')}
          </h1>
          <p className="text-muted-foreground text-center mb-8">
            {step === 1 ? t('select_language') : t('select_mode')}
          </p>

          {step === 1 ? (
            /* Language Selection */
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid grid-cols-2 gap-4"
            >
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setSelectedLanguage(lang.code)}
                  className={`relative p-6 rounded-xl border-2 transition-all ${
                    selectedLanguage === lang.code
                      ? 'border-primary bg-primary/10'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                  data-testid={`lang-${lang.code}-btn`}
                >
                  {selectedLanguage === lang.code && (
                    <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-primary flex items-center justify-center">
                      <Check className="h-4 w-4 text-primary-foreground" />
                    </div>
                  )}
                  <p className="font-heading text-2xl font-bold mb-1">{lang.native}</p>
                  <p className="text-muted-foreground text-sm">{lang.name}</p>
                </button>
              ))}
            </motion.div>
          ) : (
            /* Mode Selection */
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4"
            >
              {modes.map((modeOption) => (
                <button
                  key={modeOption.id}
                  onClick={() => setSelectedMode(modeOption.id)}
                  className={`w-full relative p-6 rounded-xl border-2 transition-all text-left flex items-start gap-4 ${
                    selectedMode === modeOption.id
                      ? 'border-primary bg-primary/10'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                  data-testid={`mode-${modeOption.id}-btn`}
                >
                  <div className={`h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    selectedMode === modeOption.id ? 'bg-primary' : 'bg-secondary'
                  }`}>
                    <modeOption.icon className={`h-6 w-6 ${
                      selectedMode === modeOption.id ? 'text-primary-foreground' : 'text-muted-foreground'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <p className="font-heading text-lg font-semibold mb-1">{modeOption.title}</p>
                    <p className="text-muted-foreground text-sm">{modeOption.description}</p>
                  </div>
                  {selectedMode === modeOption.id && (
                    <div className="absolute top-4 right-4 h-6 w-6 rounded-full bg-primary flex items-center justify-center">
                      <Check className="h-4 w-4 text-primary-foreground" />
                    </div>
                  )}
                </button>
              ))}
            </motion.div>
          )}

          <Button
            onClick={handleContinue}
            className="w-full mt-8 glow-primary py-6 text-lg"
            disabled={isLoading}
            data-testid="onboarding-continue-btn"
          >
            {step === 2 ? t('get_started') : 'Continue'}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default OnboardingPage;
